import { Mesh } from './Mesh';
import { VertexBuffer } from './VertexBuffer';
import { BoundingDimensions } from '../util';
import { RenderObject } from '../object/RenderObject';
import { Collection, VertexAttribute } from '../geometry';
import { Scene } from '../scene/Scene';

export class GeometryMesh extends Mesh {
  private get bounds(): BoundingDimensions {
    return this.localBounds;
  }

  /**
   * One-shot factory: build a GeometryMesh from a generated geometry Collection
   * (e.g. createCube/createSphere) with sensible default attributes.
   */
  public static fromCollection(
    name: string,
    scene: Scene,
    collection: Collection,
    attributes: VertexAttribute[] = ['position', 'normal'],
  ): GeometryMesh {
    const mesh = new GeometryMesh(name, scene.ctx, scene);
    mesh.bufferGeneratedMesh(collection, attributes);
    return mesh;
  }

  public bufferGeneratedMesh(geoCollection: Collection, attributes: VertexAttribute[]) {
    let gl = this.ctx.gl;
    let compiled = geoCollection.compile(attributes);

    this.dataLayout = {};
    if (compiled.layout.position) this.dataLayout.position = compiled.layout.position;
    if (compiled.layout.normal) this.dataLayout.normal = compiled.layout.normal;
    if (compiled.layout.color) this.dataLayout.color = compiled.layout.color;
    if (compiled.layout.texture) this.dataLayout.diffuseTexture = compiled.layout.texture;

    this._localBounds = BoundingDimensions.createFromData(
      compiled.data,
      compiled.vertexCount,
      0,
      compiled.vertexStride,
    );

    let glVertexBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, glVertexBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, compiled.data, gl.STATIC_DRAW);

    this.buffer = new VertexBuffer(
      glVertexBuffer,
      gl.TRIANGLES,
      compiled.vertexStride,
      compiled.vertexCount,
    );

    this.createVAO(gl);
  }

  public createInstance(): RenderObject {
    let obj = new RenderObject(this);
    obj.bounds = this.bounds.clone();
    obj.bounds.bind(obj.trs);
    return obj;
  }
}
